import { useState, useEffect, useRef, useCallback } from 'react';
import './ResizablePanel.css';

/**
 * ResizablePanel Component
 * Handles drag-to-resize functionality between two panels
 */
export function ResizableContainer({ children, className = '' }) {
    return (
        <div className={`resizable-container ${className}`}>
            {children}
        </div>
    );
}

export function ResizablePanel({
    children,
    width,
    minWidth = 200,
    maxWidth = 800,
    onResize,
    flex = 1,
    className = '',
    collapsed = false,
    collapsedWidth = 0
}) {
    const style = collapsed
        ? { width: collapsedWidth, flex: 'none' }
        : width
            ? { width, flex: 'none' }
            : { flex };

    return (
        <div
            className={`resizable-panel ${className} ${collapsed ? 'collapsed' : ''}`}
            style={style}
        >
            {children}
        </div>
    );
}

export function ResizeHandle({ onResize, isResizing, vertical = true, hidden = false }) {
    if (hidden) return null;
    
    return (
        <div
            className={`resize-handle ${vertical ? 'vertical' : 'horizontal'} ${isResizing ? 'resizing' : ''}`}
            onMouseDown={onResize}
        />
    );
}

/**
 * Hook to manage resize logic
 * @param {string} storageKey - localStorage key to persist width
 * @param {number} defaultWidth - default width in pixels
 * @param {number} minWidth - minimum width constraints
 * @param {number} maxWidth - maximum width constraints
 */
export function useResizable(storageKey, defaultWidth = 350, minWidth = 250, maxWidth = 600) {
    const [width, setWidth] = useState(() => {
        if (!storageKey) return defaultWidth;
        const saved = localStorage.getItem(storageKey);
        return saved ? parseInt(saved, 10) : defaultWidth;
    });

    const [isResizing, setIsResizing] = useState(false);
    const startXRef = useRef(0);
    const startWidthRef = useRef(0);

    const startResize = useCallback((e) => {
        startXRef.current = e.clientX;
        startWidthRef.current = width;
        setIsResizing(true);
        document.body.classList.add('resize-active');
    }, [width]);

    const stopResize = useCallback(() => {
        setIsResizing(false);
        document.body.classList.remove('resize-active');
        if (storageKey) {
            localStorage.setItem(storageKey, width);
        }
    }, [width, storageKey]);

    const handleResize = useCallback((e) => {
        if (!isResizing) return;

        const delta = e.clientX - startXRef.current;
        let newWidth = startWidthRef.current + delta;

        // Apply constraints
        newWidth = Math.max(minWidth, Math.min(maxWidth, newWidth));

        setWidth(newWidth);
    }, [isResizing, minWidth, maxWidth]);

    useEffect(() => {
        if (isResizing) {
            window.addEventListener('mousemove', handleResize);
            window.addEventListener('mouseup', stopResize);
        }
        return () => {
            window.removeEventListener('mousemove', handleResize);
            window.removeEventListener('mouseup', stopResize);
        };
    }, [isResizing, handleResize, stopResize]);

    return { width, startResize, isResizing };
}
